import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchFeeReportsSummary } from "../../redux/admin/fee_reports.thunk";
import {
  selectFinancialSummary,
  selectDefaultersList,
  selectFeeReportFilterContext,
  selectFeeReportsLoading,
} from "../../redux/admin/fee_reports.selector";

/* ============================================================================
   Fee Reports - Aadhav Academy
   ----------------------------------------------------------------------------
   1. Header + filter bar (one straight row, never changes size)
   2. KPI row
   3. Cross-check: report summary vs sum of student rows (monthly only)
   4. Student ledger: search + tabs, grouped table, subtotals, grand total

   Loading behaviour (no flicker / no layout shift):
     - First load      -> skeleton placeholders with the final layout size
     - Later reloads   -> previous data stays on screen; after 200 ms it dims
                          slightly and a thin progress line runs on the filter bar
     - Fast loads (<200 ms) show no loader at all
   ============================================================================ */

/* ------------------------------ constants -------------------------------- */

// Reports start from the first month that has fee records.
const START_YEAR = 2026;
const START_MONTH = 7; // July (first month with fee records)

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const REPORT_TYPES = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "overall", label: "Overall" },
];

const GROUPS = {
  aa: { key: "aa", label: "Aadhav Academy", rank: 0 },
  mnps: { key: "mnps", label: "MNPS", rank: 1 },
  other: { key: "other", label: "Other students", rank: 2 },
};

const SKELETON_ROWS = 8;

/* ------------------------------- helpers --------------------------------- */

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format as ₹1,23,456.00 (avoids "-₹0.00"). */
const money = (n) => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return inr.format(v === 0 ? 0 : v);
};

const toCents = (n) => Math.round((Number(n) || 0) * 100);
const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(String(v).replace(" ", "T"));
  return Number.isNaN(d.getTime())
    ? String(v)
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const PAYMENT_TYPE_LABEL = { tuition: "Tuition", advance: "Advance" };
const typeLabel = (t) =>
  PAYMENT_TYPE_LABEL[t] || String(t || "Payment").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
const shortPeriod = (m, y) => (m && y ? `${MONTHS[m - 1].slice(0, 3)} ${y}` : "—");

const groupOf = (admissionNumber) => {
  const code = String(admissionNumber || "").toUpperCase();
  if (code.startsWith("AA")) return GROUPS.aa;
  if (code.startsWith("MNPS")) return GROUPS.mnps;
  return GROUPS.other;
};

const sumRows = (rows) =>
  rows.reduce(
    (t, r) => {
      t.plan += r.plan;
      t.discount += r.discount;
      t.net += r.net;
      t.paid += r.paid;
      t.balance += r.balance;
      return t;
    },
    { plan: 0, discount: 0, net: 0, paid: 0, balance: 0 }
  );

/**
 * Turn the API payload into one flat list of numeric rows.
 * "Paid" follows the API lists (paid_students / pending_students) so the tabs,
 * table and server counts can never disagree.
 */
const normalizeStudents = (raw) => {
  const lists = raw?.lists;
  const tag = (arr, isPaid) => (arr || []).map((s) => ({ ...s, __paid: isPaid }));

  const source = lists
    ? [...tag(lists.paid_students, true), ...tag(lists.pending_students, false)]
    : (Array.isArray(raw) ? raw : raw?.defaulters_list || []).map((s) => ({ ...s, __paid: null }));

  return source.map((st) => {
    const plan = Number(st.plan_amount ?? st.net_fee_plan ?? 0) || 0;
    const discount = Number(st.plan_discount ?? 0) || 0;
    const net = Number(st.net_fee_plan ?? plan - discount) || 0;
    const paid = Number(st.total_paid ?? 0) || 0;
    const balance = Number(st.balance_due ?? net - paid) || 0;
    const isPaid = st.__paid ?? (paid > 0 && balance <= 0);
    const advance = net > 0 && paid > net ? paid - net : 0;

    return { ...st, plan, discount, net, paid, balance, isPaid, advance, group: groupOf(st.admission_number) };
  });
};

/** Allowed month range for a year: not before the start month, not in the future. */
const monthRange = (year, currentYear, currentMonth) => ({
  from: year <= START_YEAR ? START_MONTH : 1,
  to: year >= currentYear ? currentMonth : 12,
});

/** True only after `flag` has stayed true for `delay` ms (prevents loader flashes). */
const useDelayedFlag = (flag, delay = 200) => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!flag) {
      setOn(false);
      return undefined;
    }
    const t = setTimeout(() => setOn(true), delay);
    return () => clearTimeout(t);
  }, [flag, delay]);
  return on;
};

/* ---------------------------- small components --------------------------- */

const Skel = ({ w = "100%", h = 14, style }) => (
  <span className="fr-skel" style={{ width: w, height: h, ...style }} aria-hidden="true" />
);

const StatusBadge = ({ paid }) => (
  <span className={`fr-badge ${paid ? "is-paid" : "is-pending"}`}>
    <i aria-hidden="true" />
    {paid ? "Paid" : "Pending"}
  </span>
);

const Contact = ({ mobile, alt }) => (
  <>
    <div>{mobile ? <a className="fr-link" href={`tel:${mobile}`}>{mobile}</a> : "—"}</div>
    {alt && (
      <div className="fr-sub">
        Alt: <a className="fr-link" href={`tel:${alt}`}>{alt}</a>
      </div>
    )}
  </>
);

const PaidMeta = ({ st }) => (
  <>
    {st.paid > 0 && (
      <div className="fr-sub">
        <span className="fr-chip">{st.payment_mode || "Mode not recorded"}</span>
        {st.payment_date && <span> {fmtDate(st.payment_date)}</span>}
      </div>
    )}
    {st.advance > 0 && (
      <div className="fr-sub">
        <span className="fr-chip is-info">Advance {money(st.advance)}</span>
      </div>
    )}
  </>
);

/* -------------------------------- page ----------------------------------- */

const FeeReports = () => {
  const dispatch = useDispatch();

  const summaryRaw = useSelector(selectFinancialSummary);
  const rawListRaw = useSelector(selectDefaultersList);
  const filterContext = useSelector(selectFeeReportFilterContext) || {};
  const loading = Boolean(useSelector(selectFeeReportsLoading));

  const today = new Date();
  const currentYear = Math.max(today.getFullYear(), START_YEAR);
  const currentMonth = today.getFullYear() < START_YEAR ? START_MONTH : today.getMonth() + 1;

  const [reportType, setReportType] = useState(filterContext.type || "monthly");
  const [year, setYear] = useState(clamp(Number(filterContext.year) || currentYear, START_YEAR, currentYear));
  const [month, setMonth] = useState(() => {
    const y = clamp(Number(filterContext.year) || currentYear, START_YEAR, currentYear);
    const r = monthRange(y, currentYear, currentMonth);
    return clamp(Number(filterContext.month) || currentMonth, r.from, r.to);
  });
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  const years = useMemo(() => {
    const list = [];
    for (let y = START_YEAR; y <= currentYear; y += 1) list.push(y);
    return list;
  }, [currentYear]);

  const range = monthRange(year, currentYear, currentMonth);
  const monthOptions = MONTHS.slice(range.from - 1, range.to).map((label, i) => ({
    value: range.from + i,
    label,
  }));

  /* ---- fetching ---- */
  const requested = useRef(null);

  const runFetch = useCallback(() => {
    requested.current = {
      type: reportType,
      month: reportType === "monthly" ? month : null,
      year: reportType !== "overall" ? year : null,
    };
    dispatch(
      fetchFeeReportsSummary({
        type: reportType,
        month: reportType === "monthly" ? month : "",
        year: reportType !== "overall" ? year : "",
      })
    );
  }, [dispatch, reportType, month, year]);

  useEffect(() => {
    runFetch();
  }, [runFetch]);

  /* ---- "shown" data: only replaced when a load finishes, so the screen never
         flashes empty or zero while the next report is on its way ---- */
  const [view, setView] = useState({ ready: false, summary: null, rawList: null, params: null });
  const sawLoading = useRef(false);

  useEffect(() => {
    if (loading) {
      sawLoading.current = true;
      return;
    }
    if (!sawLoading.current) return;
    const params = requested.current;
    setView((prev) =>
      prev.ready && prev.summary === summaryRaw && prev.rawList === rawListRaw && prev.params === params
        ? prev
        : { ready: true, summary: summaryRaw, rawList: rawListRaw, params }
    );
  }, [loading, summaryRaw, rawListRaw]);

  const [updatedAt, setUpdatedAt] = useState(null);
  useEffect(() => {
    if (view.ready) setUpdatedAt(new Date());
  }, [view]);

  const ready = view.ready;
  const busy = useDelayedFlag(loading && ready, 200); // dim + progress line only on slow reloads

  const handleYear = (value) => {
    const r = monthRange(value, currentYear, currentMonth);
    setYear(value);
    setMonth((m) => clamp(m, r.from, r.to));
  };

  /* ---- derived data (always from the SHOWN data) ---- */
  const summary = view.summary || {};
  const shown = view.params || { type: reportType, month, year };

  const students = useMemo(() => normalizeStudents(view.rawList), [view.rawList]);
  const paidCount = useMemo(() => students.filter((s) => s.isPaid).length, [students]);
  const pendingCount = students.length - paidCount;
  const inactiveCount = useMemo(() => students.filter((s) => s.student_status === "inactive").length, [students]);

  // Payments received in the shown month for OTHER fee months (late / arrears / advance)
  const otherPayments = useMemo(
    () => (view.rawList?.lists?.other_month_payments || []).map((p) => ({ ...p, amount: Number(p.amount) || 0 })),
    [view.rawList]
  );
  const otherTotal = useMemo(() => otherPayments.reduce((t, p) => t + p.amount, 0), [otherPayments]);

  // Totals of ALL student rows (ignores tab/search) - used for the cross-check.
  const allTotals = useMemo(() => sumRows(students), [students]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return students.filter((s) => {
      if (tab === "paid" && !s.isPaid) return false;
      if (tab === "pending" && s.isPaid) return false;
      if (tab === "inactive" && s.student_status !== "inactive") return false;
      if (!q) return true;
      return [s.name, s.admission_number, s.mobile_no, s.alternative_no]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [students, tab, query]);

  const visibleTotals = useMemo(() => sumRows(visible), [visible]);

  const groups = useMemo(() => {
    const sorted = [...visible].sort(
      (a, b) => a.group.rank - b.group.rank || String(a.name || "").localeCompare(String(b.name || ""))
    );
    const out = [];
    sorted.forEach((row) => {
      let g = out[out.length - 1];
      if (!g || g.key !== row.group.key) {
        g = { ...row.group, rows: [] };
        out.push(g);
      }
      g.rows.push(row);
    });
    return out.map((g) => ({ ...g, totals: sumRows(g.rows) }));
  }, [visible]);

  const expected = Number(summary.total_expected) || 0;
  const received = Number(summary.total_received) || 0;
  const pending = Number(summary.total_pending) || 0;
  const rate = expected > 0 ? (received / expected) * 100 : 0;

  const checks = [
    { label: "Invoiced", summary: expected, rows: allTotals.net },
    { label: "Collected", summary: received, rows: allTotals.paid },
    { label: "Outstanding", summary: pending, rows: allTotals.balance },
  ].map((c) => ({
    ...c,
    diff: c.summary - c.rows,
    ok: Math.abs(toCents(c.summary) - toCents(c.rows)) <= 1,
  }));
  const mismatches = checks.filter((c) => !c.ok).length;

  const periodLabel =
    shown.type === "monthly"
      ? `${MONTHS[(shown.month || month) - 1]} ${shown.year || year}`
      : shown.type === "yearly"
      ? `Year ${shown.year || year}`
      : "All time";

  const hasFilters = tab !== "all" || query.trim() !== "";
  const clearFilters = () => {
    setTab("all");
    setQuery("");
  };

  const tabs = [
    { key: "all", label: "All", count: students.length },
    { key: "pending", label: "Pending", count: pendingCount },
    { key: "paid", label: "Paid", count: paidCount },
    { key: "inactive", label: "Inactive", count: inactiveCount },
  ];

  /* ------------------------------- render -------------------------------- */
  return (
    <div className="fr-page">
      <div className="fr-shell">
        {/* 1. HEADER */}
        <header className="fr-head">
          <h1 className="fr-title">Fee reports</h1>
          <p className="fr-meta">
            Aadhav Academy · {periodLabel}
            <span className="fr-updated">
              {updatedAt
                ? ` · Updated ${updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
                : ""}
            </span>
          </p>
        </header>

        {/* FILTER BAR - one straight row; fields stay in place (disabled, not hidden) */}
        <div className="fr-filterbar">
          <div className="fr-field">
            <span className="fr-label" id="fr-type-label">Report</span>
            <div className="fr-segment" role="group" aria-labelledby="fr-type-label">
              {REPORT_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  aria-pressed={reportType === t.value}
                  className={reportType === t.value ? "is-active" : ""}
                  onClick={() => setReportType(t.value)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <label className="fr-field">
            <span className="fr-label">Month</span>
            <select
              className="fr-select"
              value={reportType === "monthly" ? month : ""}
              disabled={reportType !== "monthly"}
              onChange={(e) => setMonth(Number(e.target.value))}
            >
              {reportType === "monthly" ? (
                monthOptions.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)
              ) : (
                <option value="">All months</option>
              )}
            </select>
          </label>

          <label className="fr-field">
            <span className="fr-label">Year</span>
            <select
              className="fr-select"
              value={reportType !== "overall" ? year : ""}
              disabled={reportType === "overall"}
              onChange={(e) => handleYear(Number(e.target.value))}
            >
              {reportType !== "overall" ? (
                years.map((y) => <option key={y} value={y}>{y}</option>)
              ) : (
                <option value="">All years</option>
              )}
            </select>
          </label>

          <div className="fr-actions">
            <button type="button" className="fr-btn" onClick={runFetch} disabled={loading}>
              <span className={`fr-btn-spin ${loading ? "on" : ""}`} aria-hidden="true" />
              Refresh
            </button>
            <button type="button" className="fr-btn is-ghost" onClick={() => window.print()}>
              Print
            </button>
          </div>

          <span className={`fr-progress ${busy ? "on" : ""}`} role="progressbar" aria-hidden={!busy} aria-label="Loading report" />
        </div>

        {/* 2. KPI ROW */}
        <section className={`fr-kpis ${busy ? "is-stale" : ""}`} aria-label="Summary" aria-busy={loading}>
          <div className="fr-kpi">
            <span className="fr-kpi-label">Invoiced</span>
            {ready ? <span className="fr-kpi-value">{money(expected)}</span> : <Skel w="70%" h={28} />}
            {ready ? <span className="fr-kpi-hint">Fees due · {periodLabel}</span> : <Skel w="55%" h={12} />}
          </div>
          <div className="fr-kpi">
            <span className="fr-kpi-label">Collected</span>
            {ready ? <span className="fr-kpi-value is-ok">{money(received)}</span> : <Skel w="70%" h={28} />}
            {ready ? <span className="fr-kpi-hint">Payments received</span> : <Skel w="45%" h={12} />}
          </div>
          <div className="fr-kpi is-focus">
            <span className="fr-kpi-label">Outstanding</span>
            {ready ? <span className="fr-kpi-value is-bad">{money(pending)}</span> : <Skel w="70%" h={28} />}
            {ready ? <span className="fr-kpi-hint">{pendingCount} students to follow up</span> : <Skel w="60%" h={12} />}
          </div>
          <div className="fr-kpi">
            <span className="fr-kpi-label">Collection rate</span>
            {ready ? <span className="fr-kpi-value">{rate.toFixed(1)}%</span> : <Skel w="40%" h={28} />}
            <span className="fr-bar" aria-hidden="true">
              <span style={{ width: ready ? `${clamp(rate, 0, 100)}%` : "0%" }} />
            </span>
          </div>
        </section>

        {/* 3. CROSS-CHECK */}
        {!ready ? (
          <div className="fr-check fr-check-skel" aria-hidden="true">
            <Skel w={280} h={16} />
            <Skel w={110} h={22} style={{ borderRadius: 99 }} />
          </div>
        ) : shown.type === "monthly" ? (
          <details className={`fr-check ${busy ? "is-stale" : ""}`} open>
            <summary>
              <span className="fr-check-title">Cross-check: report summary vs student rows</span>
              <span className={`fr-badge ${mismatches === 0 ? "is-paid" : "is-warn"}`}>
                <i aria-hidden="true" />
                {mismatches === 0 ? "All totals match" : `${mismatches} ${mismatches === 1 ? "difference" : "differences"}`}
              </span>
            </summary>

            <div className="fr-scroll-x">
              <table className="fr-check-table">
                <thead>
                  <tr>
                    <th scope="col">Total</th>
                    <th scope="col" className="num">Report summary</th>
                    <th scope="col" className="num">Sum of student rows</th>
                    <th scope="col" className="num">Difference</th>
                    <th scope="col">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c) => (
                    <tr key={c.label}>
                      <th scope="row">{c.label}</th>
                      <td className="num">{money(c.summary)}</td>
                      <td className="num">{money(c.rows)}</td>
                      <td className="num">{c.ok ? "—" : money(c.diff)}</td>
                      <td>
                        <span className={`fr-badge ${c.ok ? "is-paid" : "is-warn"}`}>
                          <i aria-hidden="true" />
                          {c.ok ? "Match" : "Differs"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mismatches > 0 && (
              <p className="fr-note">
                A difference is expected when payments come from students who are not in the list
                (for example inactive students or arrears), or when an advance payment exceeds the
                current invoice.
              </p>
            )}
          </details>
        ) : (
          <p className="fr-note fr-note-box">
            The totals above cover {periodLabel.toLowerCase()}. The student ledger below lists one
            month only, so the two are not compared.
          </p>
        )}

        {/* 4. STUDENT LEDGER */}
        <section className="fr-ledger" aria-label="Student ledger" aria-busy={loading}>
          <div className="fr-toolbar">
            <div>
              <h2 className="fr-h2">Student ledger</h2>
              <p className="fr-meta">
                {ready ? `Showing ${visible.length} of ${students.length} students` : "Loading students"}
              </p>
            </div>

            <div className="fr-toolbar-right">
              <div className="fr-tabs" role="group" aria-label="Filter by status">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    aria-pressed={tab === t.key}
                    className={tab === t.key ? "is-active" : ""}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label} <span className="fr-count">{ready ? t.count : "–"}</span>
                  </button>
                ))}
              </div>
              <label className="fr-search">
                <span className="fr-sr">Search students</span>
                <input
                  type="search"
                  placeholder="Search name, ID or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
            </div>
          </div>

          {!ready ? (
            /* first load: skeleton with the same row height as the real table */
            <div className="fr-skel-table" role="status" aria-label="Loading student ledger">
              {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <div className="fr-skel-row" key={i}>
                  <span className="fr-skel-cell is-wide"><Skel w="60%" h={14} /><Skel w="35%" h={10} /></span>
                  <span className="fr-skel-cell"><Skel w="50%" h={14} /></span>
                  <span className="fr-skel-cell"><Skel w="70%" h={14} /></span>
                  <span className="fr-skel-cell"><Skel w="60%" h={14} /></span>
                  <span className="fr-skel-cell"><Skel w="60%" h={14} /></span>
                  <span className="fr-skel-cell"><Skel w="55%" h={22} style={{ borderRadius: 99 }} /></span>
                </div>
              ))}
            </div>
          ) : groups.length === 0 ? (
            <div className="fr-state">
              <strong>{hasFilters ? "No students match these filters" : "No student records for this period"}</strong>
              {hasFilters ? (
                <button type="button" className="fr-btn is-ghost" onClick={clearFilters}>Clear filters</button>
              ) : (
                <span>Try a different month or year.</span>
              )}
            </div>
          ) : (
            <div className={busy ? "is-stale" : ""}>
              {/* DESKTOP TABLE */}
              <div className="fr-table-wrap">
                <table className="fr-table">
                  <thead>
                    <tr>
                      <th scope="col">Student</th>
                      <th scope="col">Grade</th>
                      <th scope="col">Parent contact</th>
                      <th scope="col" className="num">Plan</th>
                      <th scope="col" className="num">Discount</th>
                      <th scope="col" className="num">Net fee</th>
                      <th scope="col" className="num">Paid</th>
                      <th scope="col" className="num">Balance</th>
                      <th scope="col">Status</th>
                    </tr>
                  </thead>

                  {groups.map((g) => (
                    <tbody key={g.key}>
                      {/* group header row doubles as the group subtotal */}
                      <tr className="fr-group">
                        <th scope="colgroup" colSpan={3}>
                          {g.label} <span className="fr-count">{g.rows.length}</span>
                        </th>
                        <td className="num">{money(g.totals.plan)}</td>
                        <td className="num">{g.totals.discount > 0 ? `−${money(g.totals.discount)}` : "—"}</td>
                        <td className="num">{money(g.totals.net)}</td>
                        <td className="num">{money(g.totals.paid)}</td>
                        <td className="num">{money(g.totals.balance)}</td>
                        <td />
                      </tr>

                      {g.rows.map((st) => (
                        <tr key={st.student_id}>
                          <td>
                            <div className="fr-name">
                              {st.name}
                              {st.student_status === "inactive" && <span className="fr-chip is-inactive" title="Inactive student - shown because a fee was paid">Inactive</span>}
                            </div>
                            <div className="fr-sub">ID {st.admission_number || "—"}</div>
                          </td>
                          <td>{st.grade || "—"}</td>
                          <td><Contact mobile={st.mobile_no} alt={st.alternative_no} /></td>
                          <td className="num">{money(st.plan)}</td>
                          <td className="num">{st.discount > 0 ? `−${money(st.discount)}` : "—"}</td>
                          <td className="num fr-strong">{money(st.net)}</td>
                          <td className="num">
                            <div className={st.paid > 0 ? "is-ok" : ""}>{money(st.paid)}</div>
                            <PaidMeta st={st} />
                          </td>
                          <td className={`num fr-strong ${st.isPaid ? "is-ok" : "is-bad"}`}>{money(st.balance)}</td>
                          <td><StatusBadge paid={st.isPaid} /></td>
                        </tr>
                      ))}
                    </tbody>
                  ))}

                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={3}>
                        Total · {visible.length} {visible.length === 1 ? "student" : "students"}
                      </th>
                      <td className="num">{money(visibleTotals.plan)}</td>
                      <td className="num">{visibleTotals.discount > 0 ? `−${money(visibleTotals.discount)}` : "—"}</td>
                      <td className="num">{money(visibleTotals.net)}</td>
                      <td className="num is-ok">{money(visibleTotals.paid)}</td>
                      <td className="num is-bad">{money(visibleTotals.balance)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="fr-legend">Net fee = Plan − Discount &nbsp;·&nbsp; Balance = Net fee − Paid</p>

              {/* MOBILE CARDS */}
              <div className="fr-cards">
                {groups.map((g) => (
                  <div key={g.key}>
                    <div className="fr-card-group">
                      <span>{g.label} <span className="fr-count">{g.rows.length}</span></span>
                      <span className="fr-num">Balance {money(g.totals.balance)}</span>
                    </div>

                    {g.rows.map((st) => (
                      <article key={st.student_id} className={`fr-card ${st.isPaid ? "is-paid" : "is-pending"}`}>
                        <div className="fr-card-top">
                          <div>
                            <div className="fr-name">
                              {st.name}
                              {st.student_status === "inactive" && <span className="fr-chip is-inactive" title="Inactive student - shown because a fee was paid">Inactive</span>}
                            </div>
                            <div className="fr-sub">ID {st.admission_number || "—"} · Grade {st.grade || "—"}</div>
                          </div>
                          <StatusBadge paid={st.isPaid} />
                        </div>

                        <div className="fr-card-contact"><Contact mobile={st.mobile_no} alt={st.alternative_no} /></div>

                        <dl className="fr-math">
                          <div><dt>Plan</dt><dd>{money(st.plan)}</dd></div>
                          {st.discount > 0 && <div><dt>Discount</dt><dd>−{money(st.discount)}</dd></div>}
                          <div><dt>Net fee</dt><dd>{money(st.net)}</dd></div>
                          <div><dt>Paid</dt><dd className={st.paid > 0 ? "is-ok" : ""}>{money(st.paid)}</dd></div>
                          <div className="is-total">
                            <dt>Balance</dt>
                            <dd className={st.isPaid ? "is-ok" : "is-bad"}>{money(st.balance)}</dd>
                          </div>
                        </dl>
                        <PaidMeta st={st} />
                      </article>
                    ))}
                  </div>
                ))}

                <div className="fr-card-total">
                  <div className="fr-card-total-title">Total · {visible.length} students</div>
                  <div><span>Net fee</span><span className="fr-num">{money(visibleTotals.net)}</span></div>
                  <div><span>Paid</span><span className="fr-num">{money(visibleTotals.paid)}</span></div>
                  <div><span>Balance</span><span className="fr-num">{money(visibleTotals.balance)}</span></div>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* 5. CASH RECEIVED THIS MONTH FOR OTHER FEE MONTHS (monthly only) */}
        {ready && shown.type === "monthly" && (
          <section className={`fr-ledger fr-other ${busy ? "is-stale" : ""}`} aria-label="Payments received this month for other periods">
            <div className="fr-toolbar">
              <div>
                <h2 className="fr-h2">Received in {periodLabel} for other months</h2>
                <p className="fr-meta fr-other-note">
                  Late fees, arrears and advances paid in {periodLabel}. They count toward the month they were
                  paid for. Internal advance adjustments are not new cash and are excluded.
                </p>
              </div>
              <div className="fr-other-total">
                <span className="fr-label">Cash received in {periodLabel}</span>
                <strong className="fr-num">{money(summary.cash_received_in_month)}</strong>
              </div>
            </div>

            {otherPayments.length === 0 ? (
              <div className="fr-state is-compact">
                <span>No payments for other months were received in {periodLabel}.</span>
              </div>
            ) : (
              <div className="fr-scroll-x">
                <table className="fr-table">
                  <thead>
                    <tr>
                      <th scope="col">Student</th>
                      <th scope="col">Paid on</th>
                      <th scope="col">Mode</th>
                      <th scope="col">Paid for</th>
                      <th scope="col">Type</th>
                      <th scope="col">Receipt</th>
                      <th scope="col" className="num">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {otherPayments.map((p) => (
                      <tr key={p.payment_id}>
                        <td>
                          <div className="fr-name">
                            {p.name}
                            {p.student_status === "inactive" && (
                              <span className="fr-chip is-inactive" title="Inactive student">Inactive</span>
                            )}
                          </div>
                          <div className="fr-sub">ID {p.admission_number || "—"}</div>
                        </td>
                        <td>{fmtDate(p.payment_date)}</td>
                        <td><span className="fr-chip">{p.payment_mode || "Not recorded"}</span></td>
                        <td>{shortPeriod(p.target_month, p.target_year)}</td>
                        <td>{typeLabel(p.payment_type)}</td>
                        <td className="fr-sub">{p.receipt_number || "—"}</td>
                        <td className="num fr-strong is-ok">{money(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row" colSpan={6}>
                        Total for other months · {otherPayments.length} {otherPayments.length === 1 ? "payment" : "payments"}
                      </th>
                      <td className="num is-ok">{money(otherTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&display=swap');

        .fr-page {
          --bg:#f4f6f9; --surface:#fff; --ink:#0f1b2d; --muted:#5b6778;
          --line:#e3e8ef; --line-strong:#cbd5e1; --head:#f8fafc;
          --brand:#1d4ed8; --brand-soft:#eaf0ff;
          --ok:#12805c; --ok-soft:#e6f6ef;
          --bad:#b42318; --bad-soft:#fdeceb;
          --warn:#93370d; --warn-soft:#fef3e2;
          background:var(--bg); color:var(--ink); min-height:100vh;
          font-family:'IBM Plex Sans','Segoe UI',system-ui,-apple-system,sans-serif;
          font-size:14px; line-height:1.45;
        }
        .fr-page *, .fr-page *::before, .fr-page *::after { box-sizing:border-box; }
        .fr-page :focus-visible { outline:2px solid var(--brand); outline-offset:2px; }
        .fr-shell { max-width:1240px; margin:0 auto; padding:28px 24px 64px; }
        .fr-sr { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; }
        .fr-num, .num, .fr-kpi-value { font-variant-numeric:tabular-nums; }
        .is-ok { color:var(--ok); }
        .is-bad { color:var(--bad); }

        /* header */
        .fr-head { margin-bottom:16px; }
        .fr-title { margin:0; font-size:1.6rem; font-weight:700; letter-spacing:-0.01em; line-height:1.25; }
        .fr-meta { margin:2px 0 0; color:var(--muted); font-size:0.86rem; min-height:1.25em; }

        /* filter bar: every control is 40px tall and bottom-aligned = one straight line */
        .fr-filterbar {
          position:relative; overflow:hidden;
          display:flex; flex-wrap:wrap; align-items:flex-end; gap:16px;
          background:var(--surface); border:1px solid var(--line); border-radius:10px;
          padding:14px 16px 16px; margin-bottom:16px;
        }
        .fr-field { display:flex; flex-direction:column; gap:6px; margin:0; }
        .fr-label { display:block; height:16px; line-height:16px; font-size:0.75rem; font-weight:600; color:var(--muted); }
        .fr-select, .fr-search input {
          height:40px; border:1px solid var(--line-strong); border-radius:8px; background-color:var(--surface);
          padding:0 12px; font:inherit; color:var(--ink);
        }
        .fr-select {
          appearance:none; -webkit-appearance:none; min-width:150px; padding-right:34px; cursor:pointer;
          background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1.5l5 5 5-5' fill='none' stroke='%235b6778' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat:no-repeat; background-position:right 12px center;
        }
        .fr-select:disabled { background-color:var(--head); color:var(--muted); cursor:not-allowed; }
        .fr-segment, .fr-tabs { display:inline-flex; align-items:center; background:#e9edf3; border-radius:8px; padding:3px; gap:2px; height:40px; }
        .fr-segment button, .fr-tabs button {
          border:0; background:transparent; height:34px; padding:0 14px; border-radius:6px;
          font:inherit; font-weight:600; color:var(--muted); cursor:pointer; white-space:nowrap;
        }
        .fr-segment button.is-active, .fr-tabs button.is-active { background:var(--surface); color:var(--ink); box-shadow:0 1px 2px rgba(15,27,45,.12); }
        .fr-actions { margin-left:auto; display:flex; gap:8px; }
        .fr-btn {
          height:40px; min-width:104px; padding:0 18px; border-radius:8px; border:1px solid var(--brand);
          background:var(--brand); color:#fff; font:inherit; font-weight:600; cursor:pointer;
          display:inline-flex; align-items:center; justify-content:center; gap:8px;
        }
        .fr-btn:hover:not(:disabled) { background:#1840b8; }
        .fr-btn:disabled { cursor:default; }
        .fr-btn.is-ghost { background:var(--surface); color:var(--ink); border-color:var(--line-strong); }
        .fr-btn.is-ghost:hover { background:var(--head); }
        /* spinner slot is always 14px wide, so the label never shifts */
        .fr-btn-spin { width:14px; height:14px; border-radius:50%; border:2px solid transparent; flex:none; }
        .fr-btn-spin.on { border-color:rgba(255,255,255,.35); border-top-color:#fff; animation:fr-spin .8s linear infinite; }

        /* progress line: absolutely positioned, adds no height */
        .fr-progress { position:absolute; left:0; right:0; bottom:0; height:2px; overflow:hidden; opacity:0; transition:opacity .2s ease; pointer-events:none; }
        .fr-progress.on { opacity:1; }
        .fr-progress::before { content:""; position:absolute; top:0; bottom:0; width:35%; background:var(--brand); animation:fr-slide 1.1s ease-in-out infinite; }

        /* stale = previous data kept on screen while the next report loads */
        .is-stale { opacity:.55; transition:opacity .2s ease; }
        .fr-kpis, .fr-check, .fr-ledger > div { transition:opacity .2s ease; }

        /* skeletons */
        .fr-skel {
          display:block; border-radius:6px; max-width:100%;
          background:linear-gradient(90deg,#e9edf3 25%,#f4f6f9 40%,#e9edf3 60%); background-size:300% 100%;
          animation:fr-shimmer 1.4s ease-in-out infinite;
        }

        /* KPIs: fixed height so nothing jumps when numbers arrive */
        .fr-kpis { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:16px; }
        .fr-kpi { background:var(--surface); border:1px solid var(--line); border-radius:10px; padding:16px 18px; display:flex; flex-direction:column; gap:6px; min-height:116px; }
        .fr-kpi.is-focus { border-color:#f1b8b3; background:#fffafa; }
        .fr-kpi-label { font-size:0.8rem; font-weight:600; color:var(--muted); }
        .fr-kpi-value { font-size:1.55rem; font-weight:700; letter-spacing:-0.01em; line-height:28px; }
        .fr-kpi-hint { font-size:0.78rem; color:var(--muted); line-height:12px; padding-top:2px; }
        .fr-bar { display:block; height:6px; border-radius:99px; background:#e9edf3; overflow:hidden; margin-top:auto; }
        .fr-bar span { display:block; height:100%; background:var(--ok); border-radius:99px; transition:width .4s ease; }

        /* cross-check */
        .fr-check { background:var(--surface); border:1px solid var(--line); border-radius:10px; margin-bottom:16px; }
        .fr-check-skel { display:flex; justify-content:space-between; align-items:center; padding:14px 18px; min-height:52px; }
        .fr-check > summary { display:flex; justify-content:space-between; align-items:center; gap:12px; padding:14px 18px; cursor:pointer; list-style:none; min-height:52px; }
        .fr-check > summary::-webkit-details-marker { display:none; }
        .fr-check-title { font-weight:600; }
        .fr-check-table { width:100%; border-collapse:collapse; border-top:1px solid var(--line); }
        .fr-check-table th, .fr-check-table td { padding:10px 18px; text-align:left; border-bottom:1px solid var(--line); font-size:0.88rem; }
        .fr-check-table .num { text-align:right; }
        .fr-check-table thead th { background:var(--head); color:var(--muted); font-size:0.78rem; font-weight:600; }
        .fr-check-table tbody tr:last-child th, .fr-check-table tbody tr:last-child td { border-bottom:0; }
        .fr-check-table tbody th { font-weight:600; }
        .fr-note { margin:0; padding:12px 18px 16px; color:var(--muted); font-size:0.84rem; max-width:80ch; }
        .fr-note-box { background:var(--surface); border:1px solid var(--line); border-radius:10px; margin-bottom:16px; padding:12px 18px; }
        .fr-scroll-x { overflow-x:auto; }

        /* ledger */
        .fr-ledger { background:var(--surface); border:1px solid var(--line); border-radius:10px; overflow:hidden; }
        .fr-toolbar { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:12px; padding:16px 18px; border-bottom:1px solid var(--line); }
        .fr-h2 { margin:0; font-size:1.1rem; font-weight:700; }
        .fr-toolbar-right { display:flex; flex-wrap:wrap; gap:10px; align-items:center; }
        .fr-search input { width:250px; }
        .fr-count { display:inline-block; min-width:20px; padding:0 6px; border-radius:99px; background:rgba(15,27,45,.08); font-size:0.74rem; font-weight:600; text-align:center; }

        .fr-skel-table { padding:4px 18px 12px; min-height:480px; }
        .fr-skel-row { display:grid; grid-template-columns:2.2fr 1fr 1.4fr 1.2fr 1.2fr 1fr; gap:18px; align-items:center; height:58px; border-bottom:1px solid var(--line); }
        .fr-skel-row:last-child { border-bottom:0; }
        .fr-skel-cell { display:flex; flex-direction:column; gap:7px; }

        .fr-table-wrap { overflow:auto; max-height:72vh; }
        .fr-table { width:100%; border-collapse:separate; border-spacing:0; min-width:980px; }
        .fr-table th, .fr-table td { padding:11px 16px; text-align:left; vertical-align:top; border-bottom:1px solid var(--line); }
        .fr-table thead th { position:sticky; top:0; z-index:2; background:var(--head); color:var(--muted); font-size:0.78rem; font-weight:600; border-bottom:1px solid var(--line-strong); white-space:nowrap; }
        .fr-table .num { text-align:right; white-space:nowrap; }
        .fr-table tbody tr:not(.fr-group):hover td { background:#fafbfd; }
        .fr-group th, .fr-group td { background:#eef2f7; font-weight:600; border-bottom:1px solid var(--line-strong); vertical-align:middle; }
        .fr-table tfoot th, .fr-table tfoot td { background:var(--head); font-weight:700; border-top:2px solid var(--ink); border-bottom:0; }
        .fr-strong { font-weight:600; }
        .fr-name { font-weight:600; display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
        .fr-sub { font-size:0.78rem; color:var(--muted); font-weight:400; }
        .fr-link { color:var(--ink); text-decoration:none; }
        .fr-link:hover { color:var(--brand); text-decoration:underline; }
        .fr-legend { margin:0; padding:10px 18px; border-top:1px solid var(--line); color:var(--muted); font-size:0.8rem; background:var(--head); }

        .fr-chip { display:inline-block; padding:1px 7px; border-radius:4px; background:#eef2f7; border:1px solid var(--line); color:var(--ink); font-size:0.72rem; font-weight:600; }
        .fr-chip.is-inactive { background:var(--warn-soft); border-color:#f7d9a8; color:var(--warn); }
        .fr-other { margin-top:16px; }
        .fr-other .fr-table { min-width:760px; }
        .fr-other-note { max-width:70ch; }
        .fr-other-total { display:flex; flex-direction:column; align-items:flex-end; gap:2px; }
        .fr-other-total strong { font-size:1.2rem; }
        .fr-state.is-compact { min-height:0; padding:28px 24px; }
        .fr-chip.is-info { background:var(--brand-soft); border-color:#c7d7fe; color:var(--brand); }

        .fr-badge { display:inline-flex; align-items:center; gap:6px; padding:3px 10px; border-radius:99px; font-size:0.78rem; font-weight:600; white-space:nowrap; }
        .fr-badge i { width:7px; height:7px; border-radius:50%; background:currentColor; }
        .fr-badge.is-paid { background:var(--ok-soft); color:var(--ok); }
        .fr-badge.is-pending { background:var(--bad-soft); color:var(--bad); }
        .fr-badge.is-warn { background:var(--warn-soft); color:var(--warn); }

        .fr-state { display:flex; flex-direction:column; align-items:center; gap:10px; padding:56px 24px; color:var(--muted); text-align:center; min-height:240px; justify-content:center; }
        .fr-state strong { color:var(--ink); font-size:1rem; }

        @keyframes fr-spin { to { transform:rotate(360deg); } }
        @keyframes fr-shimmer { 0% { background-position:100% 0; } 100% { background-position:-100% 0; } }
        @keyframes fr-slide { 0% { left:-35%; } 100% { left:100%; } }
        @media (prefers-reduced-motion: reduce) {
          .fr-skel, .fr-btn-spin.on, .fr-progress::before { animation:none; }
          .is-stale, .fr-bar span { transition:none; }
        }

        /* mobile cards */
        .fr-cards { display:none; }

        @media (max-width:1000px) { .fr-kpis { grid-template-columns:repeat(2,1fr); } }
        @media (max-width:900px) {
          .fr-table-wrap, .fr-legend { display:none; }
          .fr-skel-row { grid-template-columns:2fr 1fr 1fr; }
          .fr-skel-cell:nth-child(n+4) { display:none; }
          .fr-cards { display:flex; flex-direction:column; gap:10px; padding:14px; }
          .fr-search, .fr-search input { width:100%; }
          .fr-toolbar-right { width:100%; }
          .fr-card-group { display:flex; justify-content:space-between; align-items:center; gap:8px; padding:10px 4px 6px; margin-top:6px; font-weight:700; border-bottom:2px solid var(--line-strong); }
          .fr-card { border:1px solid var(--line); border-left:4px solid var(--bad); border-radius:8px; padding:14px; margin-top:10px; background:var(--surface); }
          .fr-card.is-paid { border-left-color:var(--ok); }
          .fr-card-top { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
          .fr-card-contact { margin:8px 0 10px; font-size:0.86rem; }
          .fr-math { margin:0; border-top:1px dashed var(--line-strong); padding-top:8px; }
          .fr-math > div { display:flex; justify-content:space-between; padding:3px 0; font-variant-numeric:tabular-nums; }
          .fr-math dt { color:var(--muted); }
          .fr-math dd { margin:0; font-weight:600; }
          .fr-math .is-total { border-top:1px solid var(--line); margin-top:4px; padding-top:7px; font-weight:700; }
          .fr-card-total { background:var(--ink); color:#fff; border-radius:8px; padding:14px 16px; display:flex; flex-direction:column; gap:6px; margin-top:12px; }
          .fr-card-total > div { display:flex; justify-content:space-between; font-weight:600; }
          .fr-card-total-title { color:#b9c2d6; font-size:0.8rem; border-bottom:1px solid rgba(255,255,255,.15); padding-bottom:6px; }
        }
        @media (max-width:640px) {
          .fr-shell { padding:20px 14px 48px; }
          .fr-kpis { grid-template-columns:1fr; }
          .fr-kpi { min-height:0; }
          .fr-filterbar { gap:12px; }
          .fr-field { flex:1 1 140px; }
          .fr-select { width:100%; min-width:0; }
          .fr-segment { width:100%; }
          .fr-segment button { flex:1; padding:0 8px; }
          .fr-actions { margin-left:0; width:100%; }
          .fr-actions .fr-btn { flex:1; }
        }

        @media print {
          .fr-page { background:#fff; }
          .fr-filterbar, .fr-toolbar-right, .fr-cards { display:none !important; }
          .fr-table-wrap { display:block !important; max-height:none; overflow:visible; }
          .fr-legend { display:block !important; }
          .fr-table { min-width:0; font-size:11px; }
          .fr-table thead th { position:static; }
          .is-stale { opacity:1; }
        }
      `}</style>
    </div>
  );
};

export default FeeReports;
